import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AuthService } from "../services/auth.service";
import { ProfileService } from "../services/profile.service";
import { metricsService } from "../services/metrics.service";
import { dashboardService } from "../services/dashboard.service";
import MainLayout from "../components/layouts/MainLayout";
import { Select } from "../components/ui/Select";
import { DatePicker } from "../components/ui/DatePicker";
import { MetricSelector } from "../components/customReporting/MetricSelector";
import { CptCodeSelector } from "../components/customReporting/CptCodeSelector";
import { SystemDenialsWidget } from "../components/customReporting/SystemDenialsWidget";
import { PayerDenialComparisonWidget } from "../components/customReporting/PayerDenialComparisonWidget";
import { DenialRateWidget } from "../components/customReporting/DenialRateWidget";
import { PatientFilterWidget } from "../components/customReporting/PatientFilterWidget";
import { SearchableSelect } from "../components/ui/SearchableSelect";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import type { PeriodType } from "../services/dashboard.service";
import ExcelJS from "exceljs";
import { hasAccess, ROLES } from "../utils/roles";

const AR_COLUMNS = ["0-30", "31-60", "61-90", "91-120", "121-180", "181-365", "365+"];

const formatARCurrency = (value: string | number | undefined): string => {
  const num = parseFloat(String(value ?? "0"));
  if (isNaN(num)) return "$0";
  return `$${num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const MONTH_ORDER: Record<string, number> = {
  January: 0,
  February: 1,
  March: 2,
  April: 3,
  May: 4,
  June: 5,
  July: 6,
  August: 7,
  September: 8,
  October: 9,
  November: 10,
  December: 11,
};

// Parse "MM.DD.YY-MM.DD.YY" (weekly) or "Month YYYY" (monthly) to timestamp
const parseARKey = (key: string): number => {
  if (key.includes(".")) {
    // Weekly/Daily: take start date "MM.DD.YY"
    const startPart = key.split("-")[0];
    const parts = startPart.split(".");
    if (parts.length === 3) {
      const [mm, dd, yy] = parts;
      return new Date(2000 + parseInt(yy, 10), parseInt(mm, 10) - 1, parseInt(dd, 10)).getTime();
    }
  }
  // Monthly: "Month YYYY"
  const parts = key.split(" ");
  if (parts.length === 2 && MONTH_ORDER[parts[0]] !== undefined) {
    return new Date(parseInt(parts[1], 10), MONTH_ORDER[parts[0]], 1).getTime();
  }
  return new Date(key).getTime() || 0;
};

// Helpers for date formatting
const formatDate = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = now;
  return { startDate: formatDate(start), endDate: formatDate(end) };
};

export const CustomReportingPage: React.FC = () => {
  const navigate = useNavigate();
  const user = AuthService.getCurrentUser();

  const userRoles = ProfileService.getUserProfileFromStorage()?.roles ?? [];
  const crPageRoles = [ROLES.CUSTOM_REPORTING_ADMIN, ROLES.CUSTOM_REPORTING_USER];
  const canTimeSeries = hasAccess(userRoles, [
    ROLES.CUSTOM_REPORTING_WIDGET_TIME_SERIES,
    ...crPageRoles,
  ]);
  const canDenialRate = hasAccess(userRoles, [
    ROLES.CUSTOM_REPORTING_WIDGET_DENIAL_RATE,
    ...crPageRoles,
  ]);
  const canPatientFilter = hasAccess(userRoles, [
    ROLES.CUSTOM_REPORTING_WIDGET_PATIENT_FILTER,
    ...crPageRoles,
  ]);
  const canTrendingAR = hasAccess(userRoles, [
    ROLES.CUSTOM_REPORTING_WIDGET_TRENDING_AR,
    ...crPageRoles,
  ]);

  const [periodType, setPeriodType] = useState<PeriodType>("current_month");
  const [appliedPeriodType, setAppliedPeriodType] = useState<PeriodType>("current_month");
  const [dateRange, setDateRange] = useState(() => getDefaultMonthRange());
  const [appliedDateRange, setAppliedDateRange] = useState(() => getDefaultMonthRange());
  const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(new Set());
  const [appliedMetrics, setAppliedMetrics] = useState<Set<string>>(new Set());
  const [selectedDoctor, setSelectedDoctor] = useState<string>("all");
  const [appliedDoctor, setAppliedDoctor] = useState<string>("all");
  const [selectedPayerRole, setSelectedPayerRole] = useState<string>("all");
  const [appliedPayerRole, setAppliedPayerRole] = useState<string>("all");
  const [selectedCptCodes, setSelectedCptCodes] = useState<Set<string>>(new Set());
  const [appliedCptCodes, setAppliedCptCodes] = useState<Set<string>>(new Set());

  // Fetch available metrics (only if user can access Time Series widget)
  const { data: metrics = [], isLoading: isLoadingMetrics } = useQuery({
    queryKey: ["custom-reporting-metrics"],
    queryFn: () => metricsService.getMetrics(),
    enabled: canTimeSeries,
  });

  // Initialize all metrics as selected on first load
  useEffect(() => {
    if (metrics.length > 0 && selectedMetrics.size === 0 && appliedMetrics.size === 0) {
      const allMetricIds = new Set(metrics.map((m) => m.metricId));
      setSelectedMetrics(allMetricIds);
      setAppliedMetrics(allMetricIds);
    }
  }, [metrics]);

  // Fetch available doctors
  const { data: doctors = [] } = useQuery({
    queryKey: ["custom-reporting-doctors"],
    queryFn: () => metricsService.getDoctors(),
  });

  // Fetch available payer roles
  const { data: payerRolesResponse } = useQuery({
    queryKey: ["custom-reporting-payer-roles"],
    queryFn: () => metricsService.getPayerRoles(),
  });

  const payerRoles = payerRolesResponse?.codes || [];

  // Fetch available CPT codes
  const { data: cptCodesResponse } = useQuery({
    queryKey: ["custom-reporting-cpt-codes"],
    queryFn: () => metricsService.getCptCodes(),
  });

  const cptCodes = cptCodesResponse?.codes || [];

  // Map doctors to their fullNames for SearchableSelect
  const doctorNames = useMemo(() => {
    return (doctors ?? []).map((doctor) => doctor.fullName);
  }, [doctors]);

  // Compute start/end date from applied period selector
  const { startDate, endDate } = useMemo(() => {
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    if (appliedPeriodType === "custom") {
      return { startDate: appliedDateRange.startDate, endDate: appliedDateRange.endDate };
    }

    if (appliedPeriodType === "current_week") {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const monday = new Date(now);
      monday.setDate(now.getDate() - daysToMonday);
      return { startDate: fmt(monday), endDate: fmt(now) };
    }

    if (appliedPeriodType === "previous_week") {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const currentMonday = new Date(now);
      currentMonday.setDate(now.getDate() - daysToMonday);

      const prevMonday = new Date(currentMonday);
      prevMonday.setDate(currentMonday.getDate() - 7);

      const prevSunday = new Date(currentMonday);
      prevSunday.setDate(currentMonday.getDate() - 1);

      return { startDate: fmt(prevMonday), endDate: fmt(prevSunday) };
    }

    if (appliedPeriodType === "previous_month") {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: fmt(start), endDate: fmt(end) };
    }

    if (appliedPeriodType === "year_to_date") {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 1); // January 1st of current year
      return { startDate: fmt(start), endDate: fmt(now) };
    }

    if (appliedPeriodType === "last_year") {
      const now = new Date();
      const start = new Date(now.getFullYear() - 1, 0, 1); // January 1st of last year
      const end = new Date(now.getFullYear() - 1, 11, 31); // December 31st of last year
      return { startDate: fmt(start), endDate: fmt(end) };
    }

    if (appliedPeriodType === "last_90_days") {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - 90);
      return { startDate: fmt(start), endDate: fmt(now) };
    }

    // current_month default
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { startDate: fmt(start), endDate: fmt(now) };
  }, [appliedPeriodType, appliedDateRange]);

  // Check if there are pending changes
  const hasChanges = useMemo(() => {
    // Check if metrics changed
    if (selectedMetrics.size !== appliedMetrics.size) return true;
    const selectedArray = Array.from(selectedMetrics);
    const appliedArray = Array.from(appliedMetrics);
    if (selectedArray.some((metric) => !appliedMetrics.has(metric))) return true;
    if (appliedArray.some((metric) => !selectedMetrics.has(metric))) return true;

    // Check if doctor changed (single select or All)
    if (selectedDoctor !== appliedDoctor) return true;

    // Check if payer role changed (single select or All)
    if (selectedPayerRole !== appliedPayerRole) return true;

    // Check if CPT codes changed
    if (selectedCptCodes.size !== appliedCptCodes.size) return true;
    const selectedCodesArray = Array.from(selectedCptCodes);
    const appliedCodesArray = Array.from(appliedCptCodes);
    if (selectedCodesArray.some((code) => !appliedCptCodes.has(code))) return true;
    if (appliedCodesArray.some((code) => !selectedCptCodes.has(code))) return true;

    // Check if period type changed
    if (periodType !== appliedPeriodType) return true;

    // Check if custom date range changed (only when custom period is selected)
    if (periodType === "custom") {
      if (
        dateRange.startDate !== appliedDateRange.startDate ||
        dateRange.endDate !== appliedDateRange.endDate
      ) {
        return true;
      }
    }

    return false;
  }, [
    selectedMetrics,
    appliedMetrics,
    selectedDoctor,
    appliedDoctor,
    selectedPayerRole,
    appliedPayerRole,
    selectedCptCodes,
    appliedCptCodes,
    periodType,
    appliedPeriodType,
    dateRange,
    appliedDateRange,
  ]);

  // Apply changes handler
  const handleApply = () => {
    setAppliedMetrics(new Set(selectedMetrics));
    setAppliedDoctor(selectedDoctor);
    setAppliedPayerRole(selectedPayerRole);
    setAppliedCptCodes(new Set(selectedCptCodes));
    setAppliedPeriodType(periodType);
    setAppliedDateRange(dateRange);
  };

  // Fetch data for all applied metrics (only if user can access Time Series widget)
  const { data: metricsData = [], isLoading: isLoadingData } = useQuery({
    queryKey: [
      "custom-reporting-data",
      Array.from(appliedMetrics),
      startDate,
      endDate,
      appliedDoctor,
      appliedPayerRole,
      Array.from(appliedCptCodes),
    ],
    queryFn: () => {
      const options: {
        doctorName?: string;
        codes?: string;
        payerRole?: string;
      } = {};

      // If a specific doctor is selected, send it; otherwise omit
      if (appliedDoctor && appliedDoctor !== "all") {
        options.doctorName = appliedDoctor;
      }

      // If a specific payer role is selected, send it; otherwise omit
      if (appliedPayerRole && appliedPayerRole !== "all") {
        options.payerRole = appliedPayerRole;
      }

      // If CPT codes are selected, send them as comma-separated string
      if (appliedCptCodes.size > 0) {
        options.codes = Array.from(appliedCptCodes).join(",");
      }

      return metricsService.calculateMultipleMetrics(
        Array.from(appliedMetrics),
        startDate,
        endDate,
        1,
        options
      );
    },
    enabled: canTimeSeries && appliedMetrics.size > 0,
  });

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!metricsData || metricsData.length === 0) {
      return { series: [], categories: [] };
    }

    // Get all unique dates from all metrics
    const allDatesSet = new Set<string>();
    metricsData.forEach((metric) => {
      metric.values.forEach((point) => {
        allDatesSet.add(point.startDateTime);
      });
    });

    const categories = Array.from(allDatesSet).sort();

    // Create series for each metric
    const series = metricsData.map((metric) => {
      const dataMap = new Map(
        metric.values.map((point) => [point.startDateTime, parseFloat(point.value)])
      );

      const data = categories.map((date) => dataMap.get(date) ?? 0);

      return {
        name: metric.metricName,
        data,
      };
    });

    return { series, categories };
  }, [metricsData]);

  const formatCurrency = (value: number): string => {
    return `$${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatDateLabel = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch {
      return dateStr;
    }
  };

  const chartOptions: ApexOptions = {
    chart: {
      type: "line",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
      animations: { enabled: true },
      zoom: {
        enabled: true,
        type: "x",
        autoScaleYaxis: true,
      },
      selection: {
        enabled: true,
        type: "x",
      },
    },
    stroke: {
      width: 2,
      curve: "smooth",
    },
    colors: ["#699CBB", "#34D399", "#FBBF24", "#F87171", "#A78BFA", "#FB923C"],
    grid: {
      strokeDashArray: 3,
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: chartData.categories.map(formatDateLabel),
      labels: {
        rotate: -45,
        rotateAlways: true,
        style: {
          fontSize: "12px",
          colors: "#6b7280",
        },
      },
      axisBorder: {
        show: true,
        color: "#BCBDC5",
      },
      axisTicks: {
        show: true,
        color: "#BCBDC5",
      },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => formatCurrency(val),
        style: {
          fontSize: "12px",
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number) => formatCurrency(val),
      },
    },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "14px",
    },
  };

  // Trending A/R
  const [trendingPeriod, setTrendingPeriod] = useState<"WEEKLY" | "MONTHLY">("WEEKLY");

  const { data: trendingARData, isLoading: isLoadingTrending } = useQuery({
    queryKey: ["trending-ar", trendingPeriod],
    queryFn: () => dashboardService.getAccountsReceivablePeriod(trendingPeriod),
    enabled: canTrendingAR,
  });

  const trendingRows = useMemo(() => {
    if (!trendingARData) return [];
    return Object.entries(trendingARData)
      .sort(([a], [b]) => parseARKey(b) - parseARKey(a))
      .map(([date, metrics]) => {
        const valMap: Record<string, string | number> = {};
        metrics.forEach((m) => {
          valMap[m.metricName] = m.value;
        });
        const total = AR_COLUMNS.reduce((sum, col) => {
          return sum + (parseFloat(String(valMap[col] ?? "0")) || 0);
        }, 0);
        return { date, valMap, total };
      });
  }, [trendingARData]);

  const exportTrendingARToExcel = async () => {
    const headers = ["Date", ...AR_COLUMNS, "Total"];
    const dataRows = trendingRows.map(({ date, valMap, total }) => {
      const row: (string | number)[] = [date];
      AR_COLUMNS.forEach((col) => {
        const val = parseFloat(String(valMap[col] ?? "0")) || 0;
        const pct = total > 0 ? ((val / total) * 100).toFixed(1) : "0.0";
        row.push(`${formatARCurrency(val)} (${pct}%)`);
      });
      row.push(formatARCurrency(total));
      return row;
    });

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Trending AR");
    ws.addRow(headers);
    dataRows.forEach((row) => ws.addRow(row));

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trending-ar-${trendingPeriod.toLowerCase()}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!user) {
      navigate("/");
    }
  }, [user, navigate]);

  if (!user) {
    return null;
  }

  return (
    <MainLayout>
      {/* Main Content */}
      <main className="flex-1 min-w-0 relative md:ml-0 ml-12">
        <div className="w-full max-w-none p-4 sm:p-6 md:p-8">
          {canTimeSeries && (
            <div className="bg-white border-2 border-border-gray">
              {/* Header */}
              <div className="bg-primary-blue text-white px-4 md:px-6 py-3">
                <h3 className="font-bold text-lg">Custom Reporting</h3>
                <span className="text-xs sm:text-sm">Time Series Analysis</span>
              </div>

              {/* Filters */}
              <div className="p-4 bg-gray-50 border-b-2 border-border-gray flex flex-wrap gap-3 items-end">
                <div className="flex-shrink-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Metrics</label>
                  <MetricSelector
                    metrics={metrics}
                    selectedMetrics={selectedMetrics}
                    onSelectionChange={setSelectedMetrics}
                  />
                </div>

                <div className="flex-shrink-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Providers</label>
                  <SearchableSelect
                    options={doctorNames}
                    value={selectedDoctor}
                    onChange={setSelectedDoctor}
                    placeholder="Select Provider"
                    allOptionLabel="All Providers"
                  />
                </div>

                <div className="flex-shrink-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Payor Roles
                  </label>
                  <SearchableSelect
                    options={payerRoles}
                    value={selectedPayerRole}
                    onChange={setSelectedPayerRole}
                    placeholder="Select Payor Role"
                    allOptionLabel="All Payor Roles"
                  />
                </div>

                <div className="flex-shrink-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">CPT Codes</label>
                  <CptCodeSelector
                    cptCodes={cptCodes}
                    selectedCptCodes={selectedCptCodes}
                    onSelectionChange={setSelectedCptCodes}
                  />
                </div>

                <div className="flex-shrink-0 min-w-[80px]">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Period</label>
                  <Select
                    value={periodType}
                    onChange={(e) => setPeriodType(e.target.value as PeriodType)}
                    className="w-full"
                  >
                    <option value="current_week">Current Week</option>
                    <option value="previous_week">Previous Week</option>
                    <option value="current_month">Current Month</option>
                    <option value="previous_month">Previous Month</option>
                    <option value="last_90_days">Last 90 Days</option>
                    <option value="year_to_date">Year to Date</option>
                    <option value="last_year">Last Year</option>
                    <option value="custom">Custom Period</option>
                  </Select>
                </div>

                {periodType === "custom" && (
                  <>
                    <div className="flex-shrink-0">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Start date
                      </label>
                      <DatePicker
                        value={dateRange.startDate}
                        maxDate={dateRange.endDate || undefined}
                        onChange={(value) =>
                          setDateRange((prev) => ({
                            ...prev,
                            startDate: value,
                          }))
                        }
                      />
                    </div>
                    <div className="flex-shrink-0">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        End date
                      </label>
                      <DatePicker
                        value={dateRange.endDate}
                        minDate={dateRange.startDate || undefined}
                        onChange={(value) =>
                          setDateRange((prev) => ({
                            ...prev,
                            endDate: value,
                          }))
                        }
                      />
                    </div>
                  </>
                )}

                {/* Apply Button */}
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={!hasChanges}
                  className="h-10 px-6 bg-primary-blue text-white text-xs sm:text-sm hover:bg-indigo-darker-blue disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0 transition-colors"
                >
                  Apply
                </button>
              </div>

              {/* Chart Content */}
              <div className="p-4 md:p-6">
                {isLoadingMetrics && (
                  <div className="text-center py-8 text-gray-500">Loading metrics...</div>
                )}

                {isLoadingData && !isLoadingMetrics && (
                  <div className="text-center py-8 text-gray-500">Loading chart data...</div>
                )}

                {!isLoadingMetrics && !isLoadingData && appliedMetrics.size === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Please select at least one metric and click Apply to display the chart.
                  </div>
                )}

                {!isLoadingMetrics &&
                  !isLoadingData &&
                  appliedMetrics.size > 0 &&
                  chartData.series.length > 0 && (
                    <div className="w-full">
                      <ReactApexChart
                        options={chartOptions}
                        series={chartData.series}
                        type="line"
                        height={400}
                      />
                    </div>
                  )}

                {!isLoadingMetrics &&
                  !isLoadingData &&
                  appliedMetrics.size > 0 &&
                  chartData.series.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No data available for the selected period.
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* System Denials — full width */}
          {canDenialRate && (
            <div className="mt-6">
              <SystemDenialsWidget />
            </div>
          )}

          {/* Payer Denials: System vs Real — full width */}
          {canDenialRate && (
            <div className="mt-6">
              <PayerDenialComparisonWidget />
            </div>
          )}

          {/* Denial Rate + Patient Filter — side by side */}
          {(canDenialRate || canPatientFilter) && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
              {canDenialRate && <DenialRateWidget />}
              {canPatientFilter && <PatientFilterWidget />}
            </div>
          )}

          {/* Trending A/R */}
          {canTrendingAR && (
            <div className="bg-white border-2 border-border-gray mt-6">
              <div className="bg-primary-blue text-white px-4 md:px-6 py-3">
                <h3 className="font-bold text-lg">Trending A/R</h3>
                <span className="text-xs sm:text-sm">
                  Accounts Receivable Trend by Aging Period
                </span>
              </div>

              {/* Controls */}
              <div className="p-4 bg-gray-50 border-b-2 border-border-gray flex flex-wrap gap-3 items-end">
                <div className="flex-shrink-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Period</label>
                  <div className="flex border-2 border-border-gray h-10">
                    <button
                      type="button"
                      onClick={() => setTrendingPeriod("WEEKLY")}
                      className={`px-4 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                        trendingPeriod === "WEEKLY"
                          ? "bg-primary-blue text-white"
                          : "bg-white text-black hover:bg-gray-50"
                      }`}
                    >
                      Weekly
                    </button>
                    <button
                      type="button"
                      onClick={() => setTrendingPeriod("MONTHLY")}
                      className={`px-4 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap border-l-2 border-border-gray ${
                        trendingPeriod === "MONTHLY"
                          ? "bg-primary-blue text-white"
                          : "bg-white text-black hover:bg-gray-50"
                      }`}
                    >
                      Monthly
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={exportTrendingARToExcel}
                  disabled={trendingRows.length === 0}
                  className="h-10 px-6 bg-primary-blue text-white text-xs sm:text-sm hover:bg-indigo-darker-blue disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0 transition-colors"
                >
                  Export to Excel
                </button>
              </div>

              <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "548px" }}>
                {isLoadingTrending ? (
                  <div className="text-center py-8 text-gray-500">Loading Trending A/R data...</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b-2 border-border-gray sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                          Date
                        </th>
                        {AR_COLUMNS.map((col) => (
                          <th
                            key={col}
                            className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase whitespace-nowrap"
                          >
                            {col}
                          </th>
                        ))}
                        <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-gray">
                      {trendingRows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={AR_COLUMNS.length + 2}
                            className="px-3 py-6 text-center text-gray-500"
                          >
                            No data available.
                          </td>
                        </tr>
                      ) : (
                        trendingRows.map(({ date, valMap, total }) => (
                          <tr key={date} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium whitespace-nowrap">{date}</td>
                            {AR_COLUMNS.map((col) => {
                              const val = parseFloat(String(valMap[col] ?? "0")) || 0;
                              const pct = total > 0 ? ((val / total) * 100).toFixed(1) : "0.0";
                              return (
                                <td key={col} className="px-3 py-2 text-right whitespace-nowrap">
                                  <div>{formatARCurrency(val)}</div>
                                  <div className="text-xs text-gray-500">{pct}%</div>
                                </td>
                              );
                            })}
                            <td className="px-3 py-2 text-right whitespace-nowrap font-semibold">
                              {formatARCurrency(total)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </MainLayout>
  );
};
